import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, ShieldAlert, ShieldCheck, RefreshCw, Power, FileWarning, ListChecks } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const BRIDGE = `${API}/api/admin/media-bridge`;

/**
 * AdminMediaBridge — Phase 1 (Feb 27, 2026).
 *
 * Quiet operator-grade surface that bridges the main NDG admin to
 * the isolated V3 media_controller module. Read + safe-recovery
 * only. No env editing, no service restarts, no bulk actions.
 *
 * Source of truth: `/app/backend/media_controller/admin_bridge.py`.
 * Endpoint pins documented in
 * `/app/backend/tests/test_media_bridge_doctrine.py`.
 */
export default function AdminMediaBridge() {
  const [health, setHealth] = useState(null);
  const [stuck, setStuck] = useState(null);
  const [doctrine, setDoctrine] = useState(null);
  const [audit, setAudit] = useState(null);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setErr(null);
    try {
      const [h, s, d, a] = await Promise.all([
        axios.get(`${BRIDGE}/health`, { withCredentials: true }),
        axios.get(`${BRIDGE}/stuck`, { withCredentials: true }),
        axios.get(`${BRIDGE}/doctrine-check`, { withCredentials: true }),
        axios.get(`${BRIDGE}/audit?limit=20`, { withCredentials: true }),
      ]);
      setHealth(h.data);
      setStuck(s.data);
      setDoctrine(d.data);
      setAudit(a.data);
    } catch (e) {
      setErr(e?.response?.status === 404
        ? "Media Bridge is OFF (MEDIA_BRIDGE_ENABLED unset)."
        : (e?.response?.data?.detail || "Failed to load bridge."));
    }
  };

  useEffect(() => { load(); }, []);

  const toggleRevoke = async (revoke) => {
    setBusy(true);
    try {
      await axios.post(`${BRIDGE}/feature/${revoke ? "revoke" : "restore"}`,
        {}, { withCredentials: true });
      await load();
    } catch (e) {
      setErr(e?.response?.data?.detail || "Action failed.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="admin-media-bridge" className="min-h-dvh bg-slate-50 dark:bg-slate-950 pb-12">
      <header className="bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-800">
        <div className="max-w-3xl mx-auto px-4 h-14 flex items-center gap-3">
          <Link to="/admin" data-testid="bridge-back" className="text-slate-500 hover:text-slate-900">
            <ArrowLeft className="w-5 h-5" />
          </Link>
          <p className="font-extrabold text-slate-900 dark:text-white">Media Operations Bridge</p>
          <span className="ml-auto text-[10px] uppercase tracking-wider text-slate-400">Phase 1 · Read + Safe Recovery</span>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 pt-5 space-y-4">
        {err && (
          <div data-testid="bridge-error" className="rounded-xl border border-amber-300 bg-amber-50 text-amber-900 p-3 text-sm">
            {err}
          </div>
        )}

        {/* Health summary */}
        <section data-testid="bridge-health" className="rounded-xl border border-slate-200 bg-white dark:bg-slate-900 p-4">
          <div className="flex items-center gap-2 mb-2">
            {doctrine?.ok ? <ShieldCheck className="w-4 h-4 text-emerald-600" /> : <ShieldAlert className="w-4 h-4 text-rose-600" />}
            <p className="font-bold text-slate-900 dark:text-white">Health</p>
          </div>
          {health ? (
            <dl className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
              <dt className="text-slate-500">MEDIA_FEATURE_ENABLED</dt>
              <dd data-testid="bridge-flag-value" className="font-mono">{String(health.feature_flag?.enabled)}</dd>
              <dt className="text-slate-500">Soft revoke active</dt>
              <dd data-testid="bridge-revoke-state" className="font-mono">{String(health.soft_revoke?.active)}</dd>
              <dt className="text-slate-500">Active jobs</dt>
              <dd className="font-mono">{health.active}</dd>
              <dt className="text-slate-500">Stuck jobs (≥30m idle)</dt>
              <dd className="font-mono">{health.stuck}</dd>
              <dt className="text-slate-500">Doctrine probe</dt>
              <dd className="font-mono">{doctrine?.ok ? "ok" : `${doctrine?.failures?.length || "?"} failed`}</dd>
            </dl>
          ) : (
            <p className="text-sm text-slate-400">Loading…</p>
          )}
          <div className="mt-3 flex gap-2">
            {health?.soft_revoke?.active ? (
              <button
                data-testid="bridge-restore-btn"
                disabled={busy}
                onClick={() => toggleRevoke(false)}
                className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-emerald-600 text-white text-xs font-bold disabled:opacity-50"
              ><Power className="w-3.5 h-3.5" /> Restore media</button>
            ) : (
              <button
                data-testid="bridge-revoke-btn"
                disabled={busy}
                onClick={() => toggleRevoke(true)}
                className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-rose-600 text-white text-xs font-bold disabled:opacity-50"
              ><Power className="w-3.5 h-3.5" /> Soft revoke</button>
            )}
            <button
              data-testid="bridge-refresh-btn"
              onClick={load}
              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg border border-slate-300 text-xs font-bold"
            ><RefreshCw className="w-3.5 h-3.5" /> Refresh</button>
          </div>
          <p className="mt-2 text-[11px] text-slate-400">
            Soft revoke writes a runtime kill-flag to <code>media_bridge_state</code>. It does NOT edit the environment.
          </p>
        </section>

        {/* Stuck jobs */}
        <section data-testid="bridge-stuck" className="rounded-xl border border-slate-200 bg-white dark:bg-slate-900 p-4">
          <div className="flex items-center gap-2 mb-2">
            <FileWarning className="w-4 h-4 text-amber-600" />
            <p className="font-bold text-slate-900 dark:text-white">Stuck media jobs</p>
            <span className="ml-auto text-xs text-slate-400">{stuck?.count ?? "—"} found · capped {stuck?.row_cap}</span>
          </div>
          {(stuck?.rows || []).length === 0 ? (
            <p className="text-sm text-slate-400">None.</p>
          ) : (
            <ul className="divide-y divide-slate-100 dark:divide-slate-800">
              {stuck.rows.map((r) => (
                <li key={r.id} className="py-2 flex items-center gap-3 text-sm">
                  <span className="font-mono text-xs text-slate-500">{(r.id || "").slice(0, 10)}</span>
                  <span className="px-1.5 py-0.5 rounded bg-slate-100 text-slate-700 text-[10px] font-bold uppercase">{r.status}</span>
                  <span className="text-xs text-slate-500">{Math.round((r.age_seconds || 0) / 60)} min idle</span>
                  <Link
                    to={`/admin/media-bridge/job/${r.id}`}
                    data-testid={`bridge-deeplink-${r.id}`}
                    className="ml-auto text-xs font-bold text-blue-600 hover:underline"
                  >Open record →</Link>
                </li>
              ))}
            </ul>
          )}
        </section>

        {/* Doctrine */}
        <section data-testid="bridge-doctrine" className="rounded-xl border border-slate-200 bg-white dark:bg-slate-900 p-4">
          <div className="flex items-center gap-2 mb-2">
            <ListChecks className="w-4 h-4 text-indigo-600" />
            <p className="font-bold text-slate-900 dark:text-white">Isolation &amp; auth doctrine check</p>
            <span className="ml-auto text-[10px] uppercase tracking-wider text-slate-400">runtime probe</span>
          </div>
          {doctrine ? (
            <ul className="space-y-1 text-xs">
              {doctrine.rules.map((r, i) => (
                <li key={i} className="flex gap-2">
                  <span className={r.passed ? "text-emerald-600" : "text-rose-600"} aria-hidden>
                    {r.passed ? "✓" : "✗"}
                  </span>
                  <span className="font-mono text-slate-700 dark:text-slate-200">{r.rule}</span>
                  {!r.passed && <span className="text-rose-500">{r.detail}</span>}
                </li>
              ))}
            </ul>
          ) : <p className="text-sm text-slate-400">Loading…</p>}
        </section>

        {/* Audit */}
        <section data-testid="bridge-audit" className="rounded-xl border border-slate-200 bg-white dark:bg-slate-900 p-4">
          <p className="font-bold text-slate-900 dark:text-white mb-2">Audit · last 20 actions</p>
          {(audit?.rows || []).length === 0 ? (
            <p className="text-sm text-slate-400">No actions recorded yet.</p>
          ) : (
            <ul className="space-y-1 text-xs font-mono">
              {audit.rows.map((r) => (
                <li key={r.id}>
                  <span className="text-slate-500">{(r.at || "").slice(11, 19)}</span>{" "}
                  <span className="font-bold">{r.action}</span>{" "}
                  {r.target?.job_id && <span className="text-blue-600">{r.target.job_id.slice(0, 10)}</span>}
                  {r.reason && <span className="text-slate-400"> · {r.reason}</span>}
                </li>
              ))}
            </ul>
          )}
        </section>
      </main>
    </div>
  );
}
